/**
 * CoSingery — **登入狀態 ＋ 免費試用額度**（`decisions/041`；0017）
 *
 * 產品負責人 2026-09-25：「i think we need the login system, i will open to public, but after login,
 *   they can have **one free song** to trial … then if they need to keep using it, they need to top up」。
 *
 * 設計：
 *   · 身份**只有一個來源** —— server 側 cookie（HttpOnly）→ 前端只可以問 `/api/auth/me`
 *     （⚠️ **唔准**將用戶 id／email 存落 localStorage 當身份 —— 咁就等於自己造一套假身份）
 *   · `deviceId` 只在**寄登入連結**時帶一次（用嚟認領登入之前嘅歌；`claimDeviceData`）
 *   · 失敗一律照實報（`rules.md` B2／B5）—— 唔准靜靜當成功
 *   · ⭐ 0018（`decisions/042`）：**邀請碼**（`?invite=` 帶入／手動輸入）喺寄連結時一齊交去 server；
 *     前端**唔准**自己判斷「邀請碼啱唔啱」（只有 server 知 hash）
 */
import { useCallback, useEffect, useState } from 'react';
import { getDeviceId } from './useChatStream';

export interface AuthUser {
  id: string;
  email: string;
}

export type AuthStatus = 'loading' | 'anon' | 'user';

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** 剩幾多首（帳號口徑；未登入 ＝ 0） */
  credits: number;
}

export interface SendLinkResult {
  ok: boolean;
  /** 真係寄咗定只係本機 devLink（明講；唔准當寄出咗） */
  emailSent?: boolean;
  /** 本機開發用（生產永遠冇） */
  devLink?: string;
  /** ⭐ 0020（`decisions/045`）：開發用一次性數碼（**只喺本機 ＋ 未設 email 配置**才回；生產永遠冇） */
  devCode?: string;
  /** ⭐ 0018 Plan B（`INVITE_DIRECT_LINK=true`）：用邀請碼換到嘅登入連結（**唔需要 email**） */
  link?: string;
  code?: string;
  detail?: string;
}

export interface VerifyCodeResult {
  ok: boolean;
  /** 失敗代號（`code_invalid`／`email_invalid`／`rate_limited`／`network_error`…） */
  code?: string;
  detail?: string;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null, credits: 0 });

  /** 問 server 而家係唔係已登入（唯一真相源） */
  const refresh = useCallback(async (): Promise<AuthState> => {
    try {
      const res = await fetch('/api/auth/me');
      const data = (await res.json().catch(() => ({}))) as { user?: AuthUser | null; credits?: number };
      const next: AuthState = data.user
        ? { status: 'user', user: data.user, credits: Number(data.credits ?? 0) }
        : { status: 'anon', user: null, credits: 0 };
      setState(next);
      return next;
    } catch {
      /** 網絡掛 → 當「未登入」但**唔准**扮登入成功 */
      const next: AuthState = { status: 'anon', user: null, credits: 0 };
      setState(next);
      return next;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** 寄一次性登入連結（帶 `deviceId` → 登入後認領舊歌；帶 `invite` → Beta 邀請碼） */
  const sendLink = useCallback(async (email: string, locale: string, invite?: string): Promise<SendLinkResult> => {
    try {
      const res = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          locale,
          deviceId: getDeviceId(),
          /** ⚠️ 只有填咗才傳（空白唔傳 ＝ 冇邀請碼） */
          ...(invite && invite.trim() ? { invite: invite.trim() } : {}),
        }),
      });
      /** server 回應形狀：成功 `{ok, emailSent, devLink?|link?, reason?}`；失敗 `{error, code?, detail?}` */
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        emailSent?: boolean;
        devLink?: string;
        link?: string;
        reason?: string;
        detail?: string;
        error?: string;
      };
      if (!res.ok) return { ok: false, code: data.error ?? `http_${res.status}`, detail: data.detail };
      return {
        ok: true,
        emailSent: data.emailSent === true,
        devLink: data.devLink,
        link: data.link,
        code: data.reason,
      };
    } catch (err) {
      return { ok: false, code: 'network_error', detail: String(err).slice(0, 120) };
    }
  }, []);

  /**
   * ⭐ **0020（`decisions/045`）：打 6 位數碼登入** ——
   *   點解：email 連結會由「處理郵件嗰個 app／瀏覽器」打開 → cookie 落喺錯嘅瀏覽器
   *   （產品負責人 2026-09-25 實測）→ 打碼就用**用戶想要嗰個**瀏覽器完成登入。
   *   ⚠️ 成功之後**一定**要 `refresh()`（身份唯一來源係 server cookie；唔准自己當登入咗）
   */
  const verifyCode = useCallback(
    async (email: string, code: string): Promise<VerifyCodeResult> => {
      try {
        const res = await fetch('/api/auth/verify-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), code, deviceId: getDeviceId() }),
        });
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
        if (!res.ok) return { ok: false, code: data.error ?? `http_${res.status}` };
        await refresh();
        return { ok: true };
      } catch (err) {
        return { ok: false, code: 'network_error', detail: String(err).slice(0, 120) };
      }
    },
    [refresh],
  );

  /** 登出（server 側刪 session；前端只係跟住 refresh） */
  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch {
      /** 就算請求失敗，cookie 都可能已經失效 → 一律重新問 server */
    }
    await refresh();
  }, [refresh]);

  return { ...state, refresh, sendLink, verifyCode, logout };
}
